export type ScrollFadeAxis = 'x' | 'y'

/** Where a scroll area's content rests: `start` for a list read from the top, `end` for a log pinned to its newest entry. */
type ScrollFadeAnchor = 'start' | 'end'

/** How one scroll area wears its fade. */
export interface ScrollFadeOptions {
  /** The axis to fade along. Read from the element's computed overflow when omitted. */
  axis?: ScrollFadeAxis
  /** `start` fades only the bottom edge of a vertical area; `end` fades both edges. A horizontal area always fades both. */
  anchor?: ScrollFadeAnchor
  /** Pixels at the end edge kept clear of content, for an area whose end sits under an overlay such as a composer. */
  endInset?: number
}

/** Which edges of a scroll area currently hide content. */
export interface ScrollFadeEdges {
  /** Content is hidden past the top (or left) edge. */
  start: boolean
  /** Content is hidden past the bottom (or right) edge. */
  end: boolean
}

export interface ScrollFadeHandle {
  /** Applies new options and re-measures. */
  update: (options: ScrollFadeOptions) => void
  /** Re-measures now. */
  measure: () => void
  /** Stops observing and removes every attribute the fade wrote. */
  detach: () => void
}

/** Marks an element that overflows; its value is the axis. */
const SCROLL_FADE_ATTRIBUTE = 'data-scroll-fade'
/** Marks an element whose top (or left) edge fades. */
const SCROLL_FADE_START_ATTRIBUTE = 'data-scroll-fade-start'
/** Marks an element whose bottom (or right) edge fades. */
const SCROLL_FADE_END_ATTRIBUTE = 'data-scroll-fade-end'

const END_INSET_PROPERTY = '--scroll-fade-end-inset'

const EDGE_TOLERANCE_PX = 1

const SCROLLING_OVERFLOW = new Set(['auto', 'scroll'])

function detectAxis(el: HTMLElement): ScrollFadeAxis {
  const style = getComputedStyle(el)
  if (SCROLLING_OVERFLOW.has(style.overflowY)) return 'y'
  if (SCROLLING_OVERFLOW.has(style.overflowX)) return 'x'
  return 'y'
}

function isReversed(el: HTMLElement, axis: ScrollFadeAxis): boolean {
  const style = getComputedStyle(el)
  if (axis === 'y') return style.flexDirection === 'column-reverse'
  return (style.flexDirection === 'row-reverse') !== (style.direction === 'rtl')
}

/** Measures which edges of a scroll area hide content; `reversed` when the scroll origin sits at the bottom or right. */
export function measureScrollEdges(
  el: HTMLElement,
  axis: ScrollFadeAxis,
  reversed = false,
): ScrollFadeEdges {
  const size = axis === 'y' ? el.clientHeight : el.clientWidth
  const extent = axis === 'y' ? el.scrollHeight : el.scrollWidth
  const offset = axis === 'y' ? el.scrollTop : el.scrollLeft
  const range = extent - size
  if (range <= EDGE_TOLERANCE_PX) return { start: false, end: false }
  const fromEnd = reversed || offset < 0
  const hiddenBefore = fromEnd ? range + offset : offset
  const hiddenAfter = fromEnd ? -offset : range - offset
  return { start: hiddenBefore > EDGE_TOLERANCE_PX, end: hiddenAfter > EDGE_TOLERANCE_PX }
}

function toggleAttribute(el: HTMLElement, name: string, value: string | null): void {
  if (value === null) {
    if (el.hasAttribute(name)) el.removeAttribute(name)
  } else if (el.getAttribute(name) !== value) {
    el.setAttribute(name, value)
  }
}

/** Fades the edges of a scroll area that hide content, re-measuring on scroll, resize and content change. */
export function attachScrollFade(
  el: HTMLElement,
  initial: ScrollFadeOptions = {},
): ScrollFadeHandle {
  let options = initial
  let axis: ScrollFadeAxis = 'y'
  let reversed = false
  let frame: number | null = null

  function paint(): void {
    const range = axis === 'y' ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth
    const edges = measureScrollEdges(el, axis, reversed)
    const fadesStart = axis === 'x' || options.anchor === 'end'
    toggleAttribute(el, SCROLL_FADE_ATTRIBUTE, range > EDGE_TOLERANCE_PX ? axis : null)
    toggleAttribute(el, SCROLL_FADE_START_ATTRIBUTE, fadesStart && edges.start ? '' : null)
    toggleAttribute(el, SCROLL_FADE_END_ATTRIBUTE, edges.end ? '' : null)
  }

  function measure(): void {
    axis = options.axis ?? detectAxis(el)
    reversed = isReversed(el, axis)
    const inset = options.endInset ?? 0
    if (inset > 0) el.style.setProperty(END_INSET_PROPERTY, `${Math.round(inset)}px`)
    else el.style.removeProperty(END_INSET_PROPERTY)
    paint()
  }

  function schedule(): void {
    if (frame !== null) return
    if (typeof requestAnimationFrame === 'undefined') {
      measure()
      return
    }
    frame = requestAnimationFrame(() => {
      frame = null
      measure()
    })
  }

  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
  const observedChildren = new Set<Element>()

  function observeChildren(): void {
    if (!resizeObserver) return
    const current = new Set(Array.from(el.children))
    for (const child of observedChildren) {
      if (current.has(child)) continue
      resizeObserver.unobserve(child)
      observedChildren.delete(child)
    }
    for (const child of current) {
      if (observedChildren.has(child)) continue
      resizeObserver.observe(child)
      observedChildren.add(child)
    }
  }

  const mutationObserver =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver(() => {
          observeChildren()
          schedule()
        })

  el.addEventListener('scroll', paint, { passive: true })
  resizeObserver?.observe(el)
  observeChildren()
  mutationObserver?.observe(el, { childList: true, subtree: true, characterData: true })
  measure()

  return {
    update(next: ScrollFadeOptions): void {
      options = next
      measure()
    },
    measure,
    detach(): void {
      el.removeEventListener('scroll', paint)
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      observedChildren.clear()
      if (frame !== null) cancelAnimationFrame(frame)
      frame = null
      el.removeAttribute(SCROLL_FADE_ATTRIBUTE)
      el.removeAttribute(SCROLL_FADE_START_ATTRIBUTE)
      el.removeAttribute(SCROLL_FADE_END_ATTRIBUTE)
      el.style.removeProperty(END_INSET_PROPERTY)
    },
  }
}
